import React, { useState, useEffect, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  FaUser,
  FaTasks,
  FaPlay,
  FaStop,
  FaTrash,
  FaSync,
  FaSpinner,
} from "react-icons/fa";
import UserIcon from "../../assets/images/worker.png";
import { getToken } from "../../api/auth";
import {
  getOrdersByStatus,
  assignOrderToWorker,
  unassignWorker,
} from "../../api/order";
import { getAllUsers } from "../../api/user";
import "./OrderAssignment.css";

const ItemTypes = { ORDER: "order" };

const OrderRow = ({ order, onOrderClick }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.ORDER,
    item: { orderNo: order.no },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }));

  return (
    <tr
      ref={drag}
      className={`oa-order-row ${isDragging ? "dragging" : ""}`}
      style={{ animationDelay: `${order.index * 0.1}s` }}
      onClick={() => onOrderClick(order)}
      title={`View details for ${order.no}`}
    >
      <td>{order.no}</td>
      <td>{order.sellToCustomerName}</td>
      <td>{order.sellToCity}</td>
      <td>{new Date(order.orderDate).toLocaleDateString()}</td>
      <td>{order.lines}</td>
    </tr>
  );
};

const AgentRow = ({
  agent,
  index,
  onDrop,
  onUnassign,
  onOrderClick,
  isLoading,
}) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.ORDER,
    drop: (item) => onDrop(item.orderNo, agent.username),
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  }));

  return (
    <div
      ref={drop}
      className={`oa-agent-cell ${isOver ? "drag-over" : ""}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="oa-agent-content">
        <img
          src={agent.photo || UserIcon}
          alt={agent.name}
          className="oa-agent-avatar"
        />
        <div className="oa-agent-info">
          <p className="oa-agent-name">
            {agent.name} ({agent.username})
          </p>
          <p className="oa-agent-details">Orders: {agent.assignedCount}</p>
        </div>
      </div>
      <div className="oa-assigned-orders">
        {agent.assignedOrders && agent.assignedOrders.length > 0 && (
          <ul className="oa-assigned-orders-list">
            {agent.assignedOrders.map((order) => {
              const totalLines = order.saleslines ? order.saleslines.length : 0;
              const completedLines = order.saleslines
                ? order.saleslines.filter((line) => line.amobilestatus === 1)
                    .length
                : 0;
              const progress =
                totalLines > 0 ? (completedLines / totalLines) * 100 : 0;
              const isDeleteDisabled = order.saleslines?.some(
                (line) => line.amobilestatus === 1
              );
              const isProcessing =
                order.startTime &&
                /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(
                  order.startTime
                );
              return (
                <li key={order.no} className="oa-assigned-order-item">
                  <button
                    className={`oa-assigned-order-button ${
                      isLoading ? "oa-loading" : ""
                    } ${isProcessing ? "processing" : ""}`}
                    title={`Order: ${order.no} for ${order.sellToCustomerName}`}
                    onClick={() => onOrderClick(order)}
                    disabled={isLoading}
                  >
                    {isLoading && <FaSpinner className="oa-spinner" />}
                    <div
                      className="oa-progress-bar"
                      style={{ width: `${progress}%` }}
                    ></div>
                    <div className="oa-order-label">
                      <span className="oa-order-no">{order.no}</span>
                      <span className="oa-customer-name">
                        {order.sellToCustomerName}
                      </span>
                    </div>
                    <span className="oa-progress-text">
                      {completedLines}/{totalLines}
                    </span>
                  </button>
                  <button
                    className="oa-unassign-btn"
                    onClick={() => onUnassign(order.no, agent.username, order)}
                    title={`Unassign ${order.no}`}
                    disabled={isDeleteDisabled || isLoading}
                  >
                    <FaTrash size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const OrderDetails = ({ order, onClose }) => {
  const totalLines = order.saleslines ? order.saleslines.length : 0;
  const completedLines = order.saleslines
    ? order.saleslines.filter((line) => line.amobilestatus === 1).length
    : 0;
  const progress = totalLines > 0 ? (completedLines / totalLines) * 100 : 0;

  return (
    <div className="oa-modal-overlay" onClick={onClose}>
      <div className="oa-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="oa-modal-header">
          <h3>Order Details - {order.no}</h3>
          <button className="oa-modal-close" onClick={onClose}>
            <span>&times;</span>
          </button>
        </div>
        <div className="oa-modal-body">
          <p>
            <strong>Order Number:</strong> {order.no}
          </p>
          <p>
            <strong>Customer:</strong> {order.sellToCustomerName}
          </p>
          <p>
            <strong>City:</strong> {order.sellToCity || "N/A"}
          </p>
          <p>
            <strong>Contact:</strong> {order.sellToContact || "N/A"}
          </p>
          <p>
            <strong>Order Date:</strong>{" "}
            {new Date(order.orderDate).toLocaleDateString()}
          </p>
          <p>
            <strong>Status:</strong>{" "}
            {order.status === 1 ? "Assigned" : "Unassigned"}
          </p>
          <p>
            <strong>Progress:</strong>
            <span className="oa-progress-text">
              {completedLines}/{totalLines} lines
            </span>
            <div className="oa-modal-progress-bar">
              <div
                className="oa-modal-progress"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </p>
          <p>
            <strong>Assigned To:</strong> {order.assignedUserId || "N/A"}
          </p>
          <div className="oa-sales-lines">
            <h4>Sales Lines</h4>
            {order.saleslines && order.saleslines.length > 0 ? (
              <table className="oa-sales-lines-table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Description</th>
                    <th>Item No</th>
                    <th>Quantity</th>
                    <th>Original Qty</th>
                    <th>Barcode</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {order.saleslines.map((line, index) => (
                    <tr key={index} className="oa-sales-line-row">
                      <td>{index + 1}</td>
                      <td>{line.description || "N/A"}</td>
                      <td>{line.no}</td>
                      <td>{Number(line.quantity || 0).toFixed(2)}</td>
                      <td>{Number(line.originalQuantity || 0).toFixed(2)}</td>
                      <td>{line.barkodi || "N/A"}</td>
                      <td>
                        <span
                          className={`oa-line-status ${
                            line.amobilestatus === 1
                              ? "oa-line-status-completed"
                              : "oa-line-status-pending"
                          }`}
                        >
                          {line.amobilestatus === 1 ? "COMPLETED" : "PENDING"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="oa-empty-state">No sales lines available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmation = ({ orderNo, onConfirm, onCancel }) => {
  return (
    <div className="oa-modal-overlay" onClick={onCancel}>
      <div
        className="oa-modal-content oa-delete-confirm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="oa-modal-header">
          <h3>Confirm Deletion</h3>
          <button className="oa-modal-close" onClick={onCancel}>
            <span>&times;</span>
          </button>
        </div>
        <div className="oa-modal-body">
          <p>Do you want to delete order {orderNo}?</p>
          <div className="oa-confirm-buttons">
            <button className="oa-glass-btn oa-btn-danger" onClick={onConfirm}>
              Yes
            </button>
            <button
              className="oa-glass-btn oa-btn-secondary"
              onClick={onCancel}
            >
              No
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderAssignment = () => {
  const token = getToken();
  const [orders, setOrders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [agentSearch, setAgentSearch] = useState("");
  const [orderSort, setOrderSort] = useState({ key: "no", direction: "asc" });
  const [agentSort, setAgentSort] = useState({ key: "name", direction: "asc" });
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Refs to keep latest data for interval
  const ordersRef = useRef([]);
  const agentsRef = useRef([]);
  const autoAssignIntervalRef = useRef(null);

  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  useEffect(() => {
    agentsRef.current = agents;
  }, [agents]);

  /** Fetch unassigned orders */
  const fetchOrders = useCallback(async () => {
    try {
      const response = await getOrdersByStatus(0);
      let ordersData = [];
      if (Array.isArray(response)) {
        ordersData = response;
      } else if (
        response &&
        response.orders &&
        Array.isArray(response.orders)
      ) {
        ordersData = response.orders;
      } else {
        ordersData = [];
      }

      const formattedOrders = ordersData.map((order, index) => ({
        no: order.no || "",
        sellToCustomerName: order.sellToCustomerName || "Unknown Customer",
        sellToCity: order.sellToCity || "N/A",
        sellToContact: order.sellToContact || "N/A",
        orderDate: order.orderDate || new Date().toISOString(),
        lines: order.saleslines ? order.saleslines.length : 0,
        index,
        saleslines: order.saleslines || [], // Include saleslines for details
        status: order.status || 0, // Ensure status is included
        assignedUserId: order.assignedUserId || null, // Include assignedUserId
      }));

      setOrders(formattedOrders);
      return true;
    } catch (err) {
      console.error("Fetch orders error:", err);
      throw new Error(
        "Failed to fetch orders: " + (err.message || "Unknown error")
      );
    }
  }, []);

  /** Fetch agents and their assigned orders */
  const fetchAgents = useCallback(async () => {
    try {
      const usersResponse = await getAllUsers();
      const usersData = Array.isArray(usersResponse) ? usersResponse : [];
      const agentsWithRole3 = usersData.filter(
        (user) => user.roleId === 3 && user.isActive === true
      );

      let assignedOrders = [];
      try {
        const assignedOrdersResponse = await getOrdersByStatus(1);
        if (Array.isArray(assignedOrdersResponse)) {
          assignedOrders = assignedOrdersResponse;
        } else if (
          assignedOrdersResponse &&
          Array.isArray(assignedOrdersResponse.orders)
        ) {
          assignedOrders = assignedOrdersResponse.orders;
        } else {
          assignedOrders = [];
        }
      } catch {
        assignedOrders = [];
      }

      const agentOrderMap = assignedOrders.reduce((acc, order) => {
        const userKey = order.assignedUserId || order.assignedUsername;
        if (userKey && order.status === 1) {
          if (!acc[userKey]) {
            acc[userKey] = { count: 0, orders: [] };
          }
          acc[userKey].orders.push(order);
          acc[userKey].count += 1;
        }
        return acc;
      }, {});

      const formattedAgents = agentsWithRole3.map((agent) => ({
        username: agent.username,
        name: agent.name,
        email: agent.email || "N/A",
        photo: agent.photo || UserIcon,
        assignedCount: agentOrderMap[agent.username]?.count || 0,
        assignedOrders: agentOrderMap[agent.username]?.orders || [],
      }));

      setAgents(formattedAgents);
      return true;
    } catch (err) {
      console.error("Fetch agents error:", err);
      throw new Error(
        "Failed to fetch agents: " + (err.message || "Unknown error")
      );
    }
  }, []);

  /** Initial data fetch */
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchOrders();
      await fetchAgents();
    } catch (err) {
      setError(err.message || "Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fetchOrders, fetchAgents]);

  useEffect(() => {
    if (token) fetchInitialData();
  }, [token, fetchInitialData]);

  /** Manual refresh */
  const handleRefresh = useCallback(async () => {
    setError(null);
    try {
      await fetchOrders();
      await fetchAgents();
    } catch (err) {
      setError(err.message || "Failed to refresh data. Please try again.");
    }
  }, [fetchOrders, fetchAgents]);

  /** Assign order */
  const handleDrop = useCallback(
    async (orderNo, username) => {
      setIsLoading(true);
      try {
        const response = await assignOrderToWorker(orderNo, username);

        if (response?.success) {
          setError(null);
          await fetchOrders();
          await fetchAgents();
        } else {
          // Use the actual message from backend!
          setError(response?.message || "Failed to assign order");
        }
      } catch (err) {
        console.error("Assign error:", err);
        // For network errors or unexpected exceptions
        const backendMessage = err.response?.data?.message || err.message;
        setError(
          backendMessage || "Failed to assign order. Please try refreshing."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [fetchOrders, fetchAgents]
  );

  /** Unassign order */
  const handleUnassign = useCallback(async (orderNo, username, order) => {
    setIsLoading(true);
    setIsDeleteConfirmOpen(true);
    setOrderToDelete({ orderNo, username, order });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (orderToDelete) {
      setIsLoading(true);
      try {
        const response = await unassignWorker(orderToDelete.orderNo);
        if (response?.success) {
          setError(null);
          await fetchOrders();
          await fetchAgents();
        } else {
          setError(response?.message || "Failed to unassign order");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to unassign order. Please try refreshing.");
      } finally {
        setIsLoading(false);
        setIsDeleteConfirmOpen(false);
        setOrderToDelete(null);
      }
    }
  }, [orderToDelete, fetchOrders, fetchAgents]);

  const cancelDelete = useCallback(() => {
    setIsLoading(false);
    setIsDeleteConfirmOpen(false);
    setOrderToDelete(null);
  }, []);

  /** Auto Assignment logic */
  const handleAutoAssign = useCallback(async () => {
    const unassignedOrders = [...ordersRef.current];
    const currentAgents = [...agentsRef.current];

    if (!unassignedOrders.length || !currentAgents.length) return;

    // Sort orders by oldest date
    const sortedOrders = unassignedOrders.sort(
      (a, b) => new Date(a.orderDate) - new Date(b.orderDate)
    );

    // Make a copy of agents with mutable counts
    const agentsCopy = currentAgents.map((a) => ({ ...a }));

    for (const order of sortedOrders) {
      // Agents with less than 3 orders
      const eligibleAgents = agentsCopy.filter((a) => a.assignedCount < 3);
      if (!eligibleAgents.length) break;

      // Pick agent with least orders
      const agentToAssign = eligibleAgents.reduce((prev, curr) =>
        prev.assignedCount <= curr.assignedCount ? prev : curr
      );

      // Assign order
      await handleDrop(order.no, agentToAssign.username);

      // Increment local count to prevent over-assign
      agentToAssign.assignedCount += 1;
    }
  }, [handleDrop]);

  const handleAutoAssignWithRefresh = useCallback(async () => {
    await handleRefresh(); // Refresh first to get latest data (mobile changes)
    await handleAutoAssign(); // Then assign using fresh data
  }, [handleRefresh, handleAutoAssign]);

  /** Auto Assignment interval */
  useEffect(() => {
    if (isAutoAssigning) {
      handleAutoAssign(); // Run assignment immediately (uses current data)
      autoAssignIntervalRef.current = setInterval(
        handleAutoAssignWithRefresh,
        30000
      ); // Refresh + assign every 30 seconds
    } else if (autoAssignIntervalRef.current) {
      clearInterval(autoAssignIntervalRef.current);
      autoAssignIntervalRef.current = null;
    }

    return () => {
      if (autoAssignIntervalRef.current)
        clearInterval(autoAssignIntervalRef.current);
    };
  }, [isAutoAssigning, handleAutoAssign, handleAutoAssignWithRefresh]);

  /** Sorting and Filtering logic */
  const handleSort = (table, key) => {
    if (table === "orders") {
      setOrderSort((prev) => ({
        key,
        direction:
          prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      }));
    } else {
      setAgentSort((prev) => ({
        key,
        direction:
          prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      }));
    }
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const valA = a[orderSort.key];
    const valB = b[orderSort.key];
    if (orderSort.key === "lines")
      return orderSort.direction === "asc" ? valA - valB : valB - valA;
    return orderSort.direction === "asc"
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });

  const sortedAgents = [...agents].sort((a, b) => {
    const valA = a[agentSort.key];
    const valB = b[agentSort.key];
    if (typeof valA === "string")
      return agentSort.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    return agentSort.direction === "asc" ? valA - valB : valB - valA;
  });

  const filteredOrders = sortedOrders.filter(
    (o) =>
      o.no?.toLowerCase()?.includes(orderSearch.toLowerCase()) ||
      o.sellToCustomerName
        ?.toLowerCase()
        ?.includes(orderSearch.toLowerCase()) ||
      o.sellToCity?.toLowerCase()?.includes(orderSearch.toLowerCase()) ||
      o.sellToContact?.toLowerCase()?.includes(orderSearch.toLowerCase())
  );

  const filteredAgents = sortedAgents.filter(
    (a) =>
      a.name?.toLowerCase().includes(agentSearch.toLowerCase()) ||
      a.username?.toLowerCase().includes(agentSearch.toLowerCase())
  );

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  if (!token) return <Navigate to="/login" replace />;

  if (loading)
    return (
      <div className="oa-container">
        <div className="oa-page-header">
          <h2 className="oa-assignment-title">Order Assignment</h2>
          <div className="oa-loading-container">
            <FaSpinner className="oa-spinner" />
            <p className="oa-loading-text">Loading orders and agents...</p>
          </div>
        </div>
      </div>
    );

  if (error && !orders.length && !agents.length)
    return (
      <div className="oa-container">
        <div className="oa-page-header">
          <h2 className="oa-assignment-title">Order Assignment</h2>
        </div>
        <div className="oa-error-message">
          {error}
          <button
            className="oa-glass-btn oa-btn-primary"
            onClick={fetchInitialData}
            style={{ marginLeft: "10px" }}
          >
            <FaSync size={16} /> Retry
          </button>
        </div>
      </div>
    );

  /** Main JSX render */
  return (
    <DndProvider backend={HTML5Backend}>
      <div className="oa-container">
        <div className="oa-page-header">
          <div className="oa-header-content">
            <div>
              <h2 className="oa-assignment-title">Order Assignment</h2>
              <p className="oa-page-subtitle">
                Assign orders to warehouse agents in real-time
              </p>
            </div>
            <div className="oa-header-buttons">
              <button
                className={`oa-glass-btn ${
                  !isAutoAssigning ? "oa-btn-primary" : "oa-btn-secondary"
                }`}
                onClick={() => setIsAutoAssigning(false)}
              >
                <FaTasks size={16} /> Manual Assignment
              </button>
              <button
                className={`oa-glass-btn ${
                  isAutoAssigning ? "oa-btn-danger" : "oa-btn-primary"
                }`}
                onClick={() => setIsAutoAssigning(!isAutoAssigning)}
              >
                {isAutoAssigning ? (
                  <>
                    <FaStop size={16} /> Stop Auto Assignment
                  </>
                ) : (
                  <>
                    <FaPlay size={16} /> Start Auto Assignment
                  </>
                )}
              </button>
              <button
                className="oa-glass-btn oa-btn-primary"
                onClick={handleRefresh}
              >
                <FaSync size={16} /> Refresh
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="oa-error-message">
            {error}
            <button
              className="oa-glass-btn oa-btn-primary"
              onClick={handleRefresh}
              style={{ marginLeft: "10px" }}
            >
              <FaSync size={16} /> Refresh
            </button>
          </div>
        )}

        <div className="oa-tables-container">
          <div className="oa-orders-table oa-glass-card">
            <h3>Unassigned Orders</h3>
            <div className="oa-filter-controls">
              <div className="oa-form-group">
                <input
                  type="text"
                  className="oa-form-input"
                  placeholder="Search by order no, customer, city, or contact..."
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                />
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  {[
                    "no",
                    "sellToCustomerName",
                    "sellToCity",
                    "orderDate",
                    "lines",
                  ].map((key) => (
                    <th key={key} onClick={() => handleSort("orders", key)}>
                      {key === "no"
                        ? "Order No"
                        : key === "sellToCustomerName"
                        ? "Customer"
                        : key === "sellToCity"
                        ? "City"
                        : key === "orderDate"
                        ? "Date"
                        : "Lines"}
                      {orderSort.key === key
                        ? orderSort.direction === "asc"
                          ? " ↑"
                          : " ↓"
                        : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <OrderRow
                      key={order.no}
                      order={order}
                      onOrderClick={handleOrderClick}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="oa-empty-state">
                      No unassigned orders
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="oa-agents-table oa-glass-card">
            <h3>Agents</h3>
            <div className="oa-filter-controls">
              <div className="oa-form-group">
                <input
                  type="text"
                  className="oa-form-input"
                  placeholder="Search by agent name or username..."
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                />
              </div>
              <div className="oa-form-group">
                <select
                  className="oa-form-select"
                  value={agentSort.key}
                  onChange={(e) => handleSort("agents", e.target.value)}
                >
                  <option value="name">Sort by Name</option>
                  <option value="assignedCount">Sort by Assigned Orders</option>
                </select>
              </div>
            </div>
            <div className="oa-agents-grid">
              {filteredAgents.length > 0 ? (
                filteredAgents.map((agent, index) => (
                  <AgentRow
                    key={agent.username}
                    agent={agent}
                    index={index}
                    onDrop={handleDrop}
                    onUnassign={handleUnassign}
                    onOrderClick={handleOrderClick}
                    isLoading={isLoading}
                  />
                ))
              ) : (
                <div className="oa-empty-state">No agents found</div>
              )}
            </div>
          </div>
        </div>
        {isModalOpen && selectedOrder && (
          <OrderDetails
            order={selectedOrder}
            onClose={() => setIsModalOpen(false)}
          />
        )}
        {isDeleteConfirmOpen && orderToDelete && (
          <DeleteConfirmation
            orderNo={orderToDelete.orderNo}
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
          />
        )}
      </div>
    </DndProvider>
  );
};

export default OrderAssignment;
